import { useActionQueue } from "@/hooks/useActionQueue";
import { ACTION_STATUSES, ActionQueueService } from "@/services/action-queue.service";
import { getJobIssueDetail, getQuoteIssueDetail, getContractorIssueDetail } from "@/data/issueIntelligence";
import { useMemo, useState, useCallback } from "react";
import { useAppData } from "@/data/AppDataContext";
import { AttentionItemDetailModal, type AttentionItem, type AttentionItemAction, type AttentionItemStatus } from "@/components/AttentionItemDetailModal";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "react-router-dom";
import { RoleContextBanner } from "@/components/RoleContextBanner";
import { RoleHandoffInbox } from "@/components/RoleHandoffInbox";
import { getInstallReadiness } from "@/components/JobWorkflowStatus";
import { getJobHealth } from "@/data/jobHealth";
import { deriveFinancialClearance } from "@/data/financialClearance";
import { format } from "date-fns";
import {
  DollarSign, ArrowRight,
  AlertTriangle, CheckCircle, XCircle, Shield, Package,
  Zap, Wallet, Wrench,
} from "lucide-react";
import { MyScheduleWidget } from "@/components/MyScheduleWidget";
import { useUISkin } from "@/data/UISkinContext";
import { CockpitHeader, StatStrip, type StatItem } from "@/components/dashboard/primitives";
import { getJobDisplayValue } from "@/data/jobPricingSelectors";

const TODAY = new Date();

export function ExecutiveCommandCenter() {
  const { jobs, issues, credits, quotes, contractors, opportunities, buildReviewEvents } = useAppData();
  const [selectedItem, setSelectedItem] = useState<AttentionItem | null>(null);
  const [itemStatuses, setItemStatuses] = useState<Record<string, AttentionItemStatus>>({});
  const [actionHistory, setActionHistory] = useState<AttentionItemAction[]>([]);
  const actionQueue = useActionQueue();
  const { skin } = useUISkin();
  const isCockpit = skin === "cockpit";

  const isCompletedWithUnsettledFinal = useCallback(
    (j: (typeof jobs)[number]) =>
      j.currentStage === "Completed" &&
      j.closeoutStatus !== "Closed" &&
      deriveFinancialClearance(j, "post_install").status !== "cleared",
    [],
  );

  // ── Executive metrics ──
  const execMetrics = useMemo(() => {
    const uncollectedCash = jobs
      .filter((j) => {
        if (!j.financials) return false;
        return (
          j.financials.paymentStatus === "Overdue" ||
          j.financials.paymentStatus === "Invoiced" ||
          j.financials.paymentStatus === "Partial Payment" ||
          isCompletedWithUnsettledFinal(j)
        );
      })
      .reduce((s, j) => s + getJobDisplayValue(j), 0);
    const blockedCount = actionQueue.items.filter((i) => i.age === "Blocked").length;
    const criticalCount = actionQueue.items.filter((i) => i.severity === "critical").length;

    const aq = actionQueue.items;
    const extractAmount = (item: AttentionItem) => {
      const match = item.details?.join(" ").match(/\$([0-9,]+)/);
      return match ? parseFloat(match[1].replace(/,/g, "")) : 0;
    };
    const riskCategories = [
      { label: "Materials", icon: Package, color: "text-warning", items: aq.filter((i) => i.details?.join(" ").toLowerCase().includes("material") || i.title.toLowerCase().includes("material") || i.title.toLowerCase().includes("tile") || i.title.toLowerCase().includes("inventory")) },
      { label: "Permits", icon: Shield, color: "text-destructive", items: aq.filter((i) => i.details?.join(" ").toLowerCase().includes("permit") || i.title.toLowerCase().includes("permit")) },
      { label: "QA", icon: CheckCircle, color: "text-primary", items: aq.filter((i) => i.details?.join(" ").toLowerCase().includes("qa") || i.title.toLowerCase().includes("qa") || i.title.toLowerCase().includes("alignment")) },
      { label: "Payments", icon: DollarSign, color: "text-warning", items: aq.filter((i) => i.details?.join(" ").toLowerCase().includes("invoice") || i.details?.join(" ").toLowerCase().includes("payment") || i.title.toLowerCase().includes("invoiced") || i.title.toLowerCase().includes("payment")) },
    ];
    const categoryTotals = riskCategories.map((cat) => ({
      ...cat,
      total: cat.items.reduce((s, i) => s + extractAmount(i), 0),
    }));
    const revenueExposed = categoryTotals.reduce((s, c) => s + c.total, 0);
    const topDriver = [...categoryTotals].sort((a, b) => b.total - a.total)[0];

    return { uncollectedCash, blockedCount, criticalCount, riskCategories: categoryTotals, revenueExposed, topDriver };
  }, [jobs, actionQueue.items, isCompletedWithUnsettledFinal]);

  // ── Build attention items ──
  const attentionData = useMemo(() => {
    const DAY_MS = 86_400_000;
    const openJobs = jobs.filter((j) => j.currentStage !== "Completed");
    const openIssues = issues.filter((i) => i.status === "Open" || i.status === "In Progress");
    const { items: aiItems, hasAIData, totalRevenueAtRisk } = actionQueue;

    let items: AttentionItem[] = [];

    if (hasAIData && aiItems.length > 0) {
      items = aiItems;
    } else {
      const fmtJobTitle = (j: typeof jobs[0], issueType: string) =>
        `${j.customerName} – ${j.address.city || "Unknown"} – ${issueType}`;
      const fmtPhone = (p: string) => {
        const d = p.replace(/\D/g, "");
        return d.length === 10 ? `(${d.slice(0,3)}) ${d.slice(3,6)}-${d.slice(6)}` : p;
      };
      const fmtJobSub = (j: typeof jobs[0]) =>
        [j.address.street, j.phone ? fmtPhone(j.phone) : "", `Job #${j.jobNumber || j.id}`].filter(Boolean).join(" • ");

      openJobs.forEach((j) => {
        if (!j.scheduledInstallDate) return;
        const daysUntil = (new Date(j.scheduledInstallDate).getTime() - TODAY.getTime()) / DAY_MS;
        if (daysUntil < 0 || daysUntil > 5) return;
        const oi = openIssues.filter((i) => i.jobId === j.id);
        const r = getInstallReadiness(j, oi, buildReviewEvents);
        if (r.status === "Ready for Install") return;
        const reasons: string[] = [];
        if (j.permitRequired && j.permitStatus !== "Approved") reasons.push("Permit pending approval");
        if (j.materialReadiness === "Missing Items") reasons.push("Materials not fulfilled");
        if (j.assignmentType === "Unassigned") reasons.push("No crew assigned");
        if (oi.length > 0) reasons.push(`${oi.length} open issue${oi.length > 1 ? "s" : ""} blocking readiness`);
        if (reasons.length === 0) reasons.push("Readiness checklist incomplete — verify dispatch prerequisites");
        const riskType = j.materialReadiness === "Missing Items" ? "Missing Inventory"
          : (j.permitRequired && j.permitStatus !== "Approved") ? "Permit Hold"
          : j.assignmentType === "Unassigned" ? "No Crew Assigned"
          : oi.length > 0 ? (oi[0].issueType) : "Dispatch Delay";
        items.push({
          id: `att-risk-${j.id}`, title: fmtJobTitle(j, riskType), entity: "Job",
          reason: fmtJobSub(j) + " · " + reasons.join(" · "),
          severity: daysUntil <= 2 ? "critical" : "warning",
          link: `/jobs/${j.id}`, age: `Installs in ${Math.ceil(daysUntil)}d`, jobId: j.id, details: reasons,
        });
      });

      openJobs.forEach((j) => {
        if (!j.permitRequired || j.permitStatus === "Approved" || j.permitStatus === "Not Required") return;
        if (items.find((i) => i.id === `att-risk-${j.id}`)) return;
        items.push({
          id: `att-permit-${j.id}`, title: fmtJobTitle(j, "Permit Hold"), entity: "Job",
          reason: fmtJobSub(j) + ` · Permit: ${j.permitStatus || "Pending"}`,
          severity: "warning", link: `/jobs/${j.id}`, jobId: j.id,
        });
      });

      openIssues.forEach((i) => {
        if (i.severity !== "Urgent") return;
        const ij = jobs.find((j) => j.id === i.jobId);
        items.push({
          id: `att-issue-${i.id}`, title: ij ? fmtJobTitle(ij, i.issueType) : `${i.issueType} — ${i.jobId}`,
          entity: "Issue", reason: ij ? fmtJobSub(ij) : i.description.slice(0, 80),
          severity: "critical", link: `/jobs/${i.jobId}`, issueId: i.id, jobId: i.jobId,
        });
      });

      jobs.filter((j) => j.financials && isCompletedWithUnsettledFinal(j)).forEach((j) => {
        items.push({
          id: `att-unpaid-${j.id}`, title: fmtJobTitle(j, "Unpaid Completion"), entity: "Job",
          reason: fmtJobSub(j) + ` · Payment: ${j.financials?.paymentStatus || "Unknown"}`,
          severity: "critical", link: `/jobs/${j.id}`, jobId: j.id,
        });
      });
    }

    const seen = new Set<string>();
    const unique = items.filter((i) => { if (seen.has(i.id)) return false; seen.add(i.id); return true; });
    const sorted = unique.sort((a, b) => a.severity !== b.severity ? (a.severity === "critical" ? -1 : 1) : 0);

    const visible = sorted
      .filter((i) => itemStatuses[i.id] !== "completed")
      .filter((i) => i.severity === "critical")
      .slice(0, 5);
    const completedCount = sorted.filter((i) => itemStatuses[i.id] === "completed").length;

    return { sorted, visible, completedCount, hasAIData, totalRevenueAtRisk };
  }, [jobs, issues, credits, quotes, actionQueue, itemStatuses]);

  const handleItemAction = useCallback(async (itemId: string, status: AttentionItemStatus, notes?: string) => {
    setItemStatuses((prev) => ({ ...prev, [itemId]: status }));
    setActionHistory((prev) => [...prev, {
      itemId,
      status,
      notes,
      acknowledgedBy: "Current User",
      timestamp: new Date().toLocaleString(),
    }]);
    const dbStatus =
      status === "completed"
        ? ACTION_STATUSES.RESOLVED
        : status === "in_progress"
          ? ACTION_STATUSES.IN_PROGRESS
          : ACTION_STATUSES.PENDING;
    const { error } = await ActionQueueService.updateStatus(itemId, dbStatus);
    if (error && status === "completed") {
      // Rule-derived/non-DB cards are persisted as acknowledged ECC records.
      const fallbackItem = attentionData.sorted.find((i) => i.id === itemId);
      if (fallbackItem) {
        await ActionQueueService.logResolved({
          source_id: fallbackItem.id,
          title: fallbackItem.title,
          context: fallbackItem.reason,
          job_id: fallbackItem.jobId,
        });
      }
    }
    await actionQueue.refresh();
  }, [actionQueue, attentionData.sorted]);

  const cockpitExecKpis: StatItem[] = [
    { label: "Revenue Exposed", value: `$${(execMetrics.revenueExposed / 1000).toFixed(1)}k`, accent: execMetrics.revenueExposed > 0 ? "bad" : undefined },
    { label: "Cash Uncollected", value: `$${(execMetrics.uncollectedCash / 1000).toFixed(1)}k`, accent: execMetrics.uncollectedCash > 0 ? "warn" : undefined },
    { label: "Blocked Jobs", value: execMetrics.blockedCount, accent: execMetrics.blockedCount > 0 ? "bad" : "good" },
    { label: "Immediate Actions", value: execMetrics.criticalCount, accent: execMetrics.criticalCount > 0 ? "bad" : "good" },
  ];

  return (
    <div className={isCockpit ? "space-y-6 max-w-[1600px] mx-auto w-full" : "space-y-4 max-w-[1300px] mx-auto"}>
      <RoleContextBanner />
      <RoleHandoffInbox />

      {/* ═══ HEADER ═══ */}
      {isCockpit ? (
        <CockpitHeader
          title="Executive Command Center"
          // subtitle={`Leadership decision surface · ${format(TODAY, "EEEE, MMMM d, yyyy")}`}
          right={
            <Link to="/reports" className="text-[13px] text-muted-foreground hover:text-foreground transition-colors inline-flex items-center gap-1.5 px-2.5 py-1.5">
              Full Reports <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          }
        />
      ) : (
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <h1 className="text-[22px] font-extrabold tracking-[-0.02em] text-foreground leading-tight">Executive Command Center</h1>
            <p className="text-xs text-muted-foreground/70 mt-0.5">
              Leadership Decision Surface · {format(TODAY, "EEEE, MMMM d, yyyy")}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/reports" className="text-xs font-medium text-primary hover:underline inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md border border-border hover:bg-muted/30 transition-colors">
              Full Reports <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
        </div>
      )}

      {/* ═══ EXECUTIVE METRICS STRIP ═══ */}
      {isCockpit ? (
        <StatStrip items={cockpitExecKpis} />
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
          {[
            { label: "Revenue Exposed", value: `$${(execMetrics.revenueExposed / 1000).toFixed(1)}k`, icon: AlertTriangle, color: "text-destructive", bg: "bg-destructive/10 border-destructive/20" },
            { label: "Cash Uncollected", value: `$${(execMetrics.uncollectedCash / 1000).toFixed(1)}k`, icon: Wallet, color: "text-warning", bg: "bg-warning/10 border-warning/20" },
            { label: "Blocked Jobs", value: execMetrics.blockedCount, icon: XCircle, color: execMetrics.blockedCount > 0 ? "text-destructive" : "text-success", bg: execMetrics.blockedCount > 0 ? "bg-destructive/10 border-destructive/20" : "bg-success/10 border-success/20" },
            { label: "Immediate Actions", value: execMetrics.criticalCount, icon: Zap, color: execMetrics.criticalCount > 0 ? "text-destructive" : "text-success", bg: execMetrics.criticalCount > 0 ? "bg-destructive/10 border-destructive/20" : "bg-success/10 border-success/20" },
          ].map((m) => (
            <Card key={m.label} className={`border ${m.bg} shadow-none`}>
              <CardContent className="p-3 text-center">
                <m.icon className={`h-3.5 w-3.5 mx-auto mb-1 ${m.color}`} />
                <p className={`text-xl font-extrabold leading-tight ${m.color}`}>{m.value}</p>
                <p className="text-[9px] text-muted-foreground font-semibold mt-0.5 uppercase tracking-wider">{m.label}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* ═══ TOP RISK DRIVER ═══ */}
      {execMetrics.topDriver && execMetrics.topDriver.total > 0 && (
        <div className="flex items-center gap-2 rounded-md border border-destructive/15 bg-destructive/[0.03] px-4 py-2">
          <AlertTriangle className="h-3.5 w-3.5 text-destructive/80 shrink-0" />
          <p className="text-xs font-semibold">
            Top Risk Driver: <span className="text-destructive/90">{execMetrics.topDriver.label} (${execMetrics.topDriver.total.toLocaleString()})</span>
          </p>
        </div>
      )}

      {/* ═══ WHAT NEEDS ATTENTION TODAY ═══ */}
      {attentionData.visible.length > 0 && (
        <Card className="border-destructive/20 bg-destructive/[0.02] shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="h-7 w-7 rounded-md bg-destructive/10 flex items-center justify-center">
                  <Zap className="h-3.5 w-3.5 text-destructive" />
                </div>
                <div>
                  <h2 className="text-sm font-bold tracking-tight">What Needs Attention Today</h2>
                  <p className="text-[11px] text-muted-foreground">
                    {attentionData.visible.length} critical items
                    {attentionData.completedCount > 0 && ` · ${attentionData.completedCount} resolved`}
                    {attentionData.hasAIData && ` · ${attentionData.totalRevenueAtRisk} at risk`}
                    {attentionData.hasAIData && <span className="ml-1 text-[9px] px-1.5 py-0.5 rounded bg-primary/10 text-primary font-semibold">AI</span>}
                  </p>
                </div>
              </div>
              <Link to="/alerts" className="text-xs text-primary hover:underline inline-flex items-center gap-1">
                View All <ArrowRight className="h-3 w-3" />
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              {attentionData.visible.map((item) => {
                const status = itemStatuses[item.id] || "active";
                const isBlocked = item.age === "Blocked";
                return (
                  <button
                    key={item.id}
                    onClick={() => setSelectedItem(item)}
                    className={`group flex items-start gap-2.5 rounded-md border p-3 transition-all text-left cursor-pointer hover:-translate-y-[1px] hover:shadow-md ${
                      isBlocked
                        ? "border-destructive/30 bg-destructive/[0.04] hover:border-destructive/40"
                        : status === "in_progress"
                          ? "border-warning/25 bg-warning/[0.03] hover:border-warning/35"
                          : "border-destructive/20 bg-destructive/[0.02] hover:border-destructive/30"
                    }`}
                  >
                    <div className={`mt-0.5 h-2.5 w-2.5 rounded-full shrink-0 ${
                      isBlocked ? "bg-destructive ring-2 ring-destructive/30"
                        : status === "in_progress" ? "bg-warning animate-pulse"
                        : "bg-destructive"
                    }`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 mb-0.5">
                        <span className="text-[13px] font-bold truncate">{item.title}</span>
                        {isBlocked && (
                          <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-destructive/10 text-destructive">Blocked</span>
                        )}
                        {status === "in_progress" && (
                          <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-warning/10 text-warning">In Progress</span>
                        )}
                        {status === "active" && !isBlocked && (
                          <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-destructive/10 text-destructive">Critical</span>
                        )}
                      </div>
                      <p className="text-[11px] text-muted-foreground leading-snug">{item.reason}</p>
                      {item.whyItMatters && (
                        <p className="text-[11px] text-warning dark:text-warning leading-snug mt-0.5 font-medium">
                          {item.whyItMatters}
                        </p>
                      )}
                      <div className="flex items-center gap-1.5 mt-1">
                        <span className="text-[9px] font-medium text-muted-foreground/70 uppercase">{item.entity}</span>
                        {item.age && !isBlocked && (
                          <span className="text-[9px] text-muted-foreground/60">· {item.age}</span>
                        )}
                      </div>
                      {(item.primaryAction || item.secondaryAction) && (
                        <div className="flex items-center gap-1.5 mt-2">
                          {item.primaryAction && (
                            <Link
                              to={item.link}
                              onClick={(e) => e.stopPropagation()}
                              className="inline-flex items-center px-3 py-1.5 text-xs font-semibold rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
                            >
                              {item.primaryAction}
                            </Link>
                          )}
                          {item.secondaryAction && (
                            <Link
                              to={item.secondaryLink || item.link}
                              onClick={(e) => e.stopPropagation()}
                              className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-lg border border-border text-muted-foreground hover:bg-muted/50 transition-colors"
                            >
                              {item.secondaryAction}
                            </Link>
                          )}
                        </div>
                      )}
                    </div>
                    <ArrowRight className="h-3.5 w-3.5 text-muted-foreground/40 group-hover:text-primary shrink-0 mt-1 transition-colors" />
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* ═══ MY SCHEDULE ═══ */}
      <MyScheduleWidget />

      {/* ═══ RISK BREAKDOWN + LEADERSHIP ACTIONS ═══ */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {/* Risk Breakdown */}
        <Card className="shadow-sm">
          <CardHeader className="pb-1.5 px-4 pt-4">
            <div className="flex items-center gap-2">
              <Shield className="h-3.5 w-3.5 text-destructive" />
              <CardTitle className="text-[13px] font-bold">Top Risk Breakdown</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            <div className="space-y-2">
              {execMetrics.riskCategories.map((cat) => (
                <div key={cat.label} className="flex items-center justify-between rounded-md border p-2.5 hover:bg-muted/20 transition-colors">
                  <div className="flex items-center gap-2">
                    <cat.icon className={`h-3.5 w-3.5 ${cat.color}`} />
                    <div>
                      <p className="text-[12px] font-semibold">{cat.label}</p>
                      <p className="text-[10px] text-muted-foreground">{cat.items.length} issue{cat.items.length !== 1 ? "s" : ""}</p>
                    </div>
                  </div>
                  <span className={`text-sm font-bold ${cat.total > 0 ? "text-destructive" : "text-muted-foreground"}`}>
                    {cat.total > 0 ? `$${(cat.total / 1000).toFixed(1)}k` : "—"}
                  </span>
                </div>
              ))}
              <div className="flex items-center justify-between rounded-md border border-destructive/15 bg-destructive/[0.03] p-2.5 mt-0.5">
                <p className="text-[12px] font-bold">Total Revenue Exposed</p>
                <span className="text-sm font-bold text-destructive">${(execMetrics.revenueExposed / 1000).toFixed(1)}k</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Leadership Actions */}
        <Card className="shadow-sm">
          <CardHeader className="pb-1.5 px-4 pt-4">
            <div className="flex items-center gap-2">
              <Zap className="h-3.5 w-3.5 text-primary" />
              <CardTitle className="text-[13px] font-bold">Leadership Actions</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            <div className="space-y-2">
              {[
                { action: "Escalate permit delays", detail: "Stalled projects will not generate revenue until resolved", icon: Shield, link: "/permitting", color: "text-destructive", priority: true },
                { action: "Push collections", detail: "Cash will remain locked until invoices are sent and balances collected", icon: DollarSign, link: "/accounting", color: "text-warning", priority: true },
                { action: "Resolve material blockers", detail: "Installs will fail without sourced materials", icon: Package, link: "/warehouse", color: "text-warning", priority: false },
                { action: "Review QA failures", detail: "Payments blocked until inspections pass", icon: Wrench, link: "/issues", color: "text-primary", priority: false },
              ].map((la) => (
                <Link key={la.action} to={la.link}>
                  <div className={`flex items-center gap-2.5 rounded-md border p-2.5 transition-all cursor-pointer hover:-translate-y-[1px] hover:shadow-sm ${la.priority ? "border-destructive/15 bg-destructive/[0.02] hover:border-destructive/25" : "hover:bg-muted/30"}`}>
                    <la.icon className={`h-3.5 w-3.5 shrink-0 ${la.color}`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <p className="text-[12px] font-semibold">{la.action}</p>
                        {la.priority && (
                          <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-destructive/10 text-destructive">High Priority</span>
                        )}
                      </div>
                      <p className="text-[10px] text-muted-foreground mt-0.5">{la.detail}</p>
                    </div>
                    <ArrowRight className="h-3 w-3 text-muted-foreground/40 shrink-0" />
                  </div>
                </Link>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Attention Item Detail Modal */}
      <AttentionItemDetailModal
        item={selectedItem}
        open={!!selectedItem}
        onOpenChange={(open) => { if (!open) setSelectedItem(null); }}
        currentStatus={selectedItem ? (itemStatuses[selectedItem.id] || "active") : "active"}
        actionHistory={actionHistory}
        jobs={jobs}
        issues={issues}
        quotes={quotes}
        contractors={contractors}
        onAction={handleItemAction}
      />
    </div>
  );
}
